'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import EnrollmentModal from '@/components/EnrollmentModal';
import EnrollmentForm from '@/components/EnrollmentForm';

export default function PricingOptions() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleEnrollClick = (planName: string) => {
    if (!isHydrated) return;
    setSelectedPlan(planName);
    setIsModalOpen(true);
  };

  return (
    <section id="pricing" className="py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-4">
            <Icon name="CurrencyRupeeIcon" size={20} variant="solid" />
            <span className="text-sm font-headline font-semibold">Flexible Options</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-headline font-bold text-foreground mb-4">
            Choose Your Path
          </h2>
          <p className="text-lg text-muted-foreground font-body max-w-3xl mx-auto">
            Get the internship completely FREE with any course, or opt for standalone internship at an affordable price
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Free with Course Option - HIGHLIGHTED */}
          <div className="relative group">
            {/* Popular Badge */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                🎉 MOST POPULAR
              </div>
            </div>

            <div className="relative bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 rounded-2xl p-8 border-4 border-green-500 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 h-full">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-headline font-bold text-foreground">
                  FREE Internship
                </h3>
                <Icon name="SparklesIcon" size={32} variant="solid" className="text-green-600" />
              </div>

              <div className="mb-6">
                <div className="flex items-baseline space-x-2 mb-2">
                  <span className="text-5xl font-headline font-bold text-green-600">₹0</span>
                  <span className="text-lg text-muted-foreground line-through">₹5,000</span>
                </div>
                <p className="text-sm font-semibold text-green-600 uppercase tracking-wide">
                  100% FREE with any course enrollment
                </p>
              </div>

              <div className="space-y-4 mb-8">
                <h4 className="font-headline font-semibold text-foreground flex items-center space-x-2">
                  <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-green-600" />
                  <span>Everything Included:</span>
                </h4>
                
                {[
                  'Access to production codebases',
                  'Build 3 beginner-friendly projects',
                  'Weekly 1-on-1 mentorship sessions',
                  'Live training programs for projects',
                  'Code reviews by industry experts',
                  'Learn complete project lifecycle',
                  'Deployment & DevOps training',
                  'Official internship certificate',
                  'LinkedIn profile optimization',
                  'Resume project additions',
                  'Flexible duration (2-6 months)'
                ].map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Icon name="CheckIcon" size={20} variant="solid" className="text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-foreground">{feature}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => handleEnrollClick('Free Internship with Course')}
                className="w-full px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-headline font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center justify-center space-x-2"
              >
                <span>Enroll in Course + Get FREE Internship</span>
                <Icon name="ArrowRightIcon" size={20} variant="solid" />
              </button>

              <p className="text-xs text-muted-foreground text-center mt-4">
                Available with any of our professional certification programs
              </p>
            </div>
          </div>

          {/* Standalone Internship Option */}
          <div className="relative bg-card rounded-2xl p-8 border border-border shadow-lg hover:shadow-xl hover:border-primary transition-all duration-300 hover:-translate-y-1 h-full">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-headline font-bold text-foreground">
                Standalone Internship
              </h3>
              <Icon name="BriefcaseIcon" size={32} variant="outline" className="text-primary" />
            </div>

            <div className="mb-6">
              <div className="flex items-baseline space-x-2 mb-2">
                <span className="text-5xl font-headline font-bold text-primary">₹5,000</span>
                <span className="text-lg text-muted-foreground">+ GST</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Internship only, without course enrollment
              </p>
            </div>

            <div className="space-y-4 mb-8">
              <h4 className="font-headline font-semibold text-foreground flex items-center space-x-2">
                <Icon name="CheckCircleIcon" size={20} variant="outline" className="text-primary" />
                <span>What's Included:</span>
              </h4>
              
              {[
                'Access to production codebases',
                'Build 2-3 beginner-friendly projects',
                'Twice a week mentorship sessions',
                'Live training programs for projects',
                'Code review feedback',
                'Learn project architecture',
                'Basic deployment training',
                'Internship completion certificate',
                'Portfolio project guidance',
                'Fixed 2-month duration'
              ].map((feature, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <Icon name="CheckIcon" size={20} variant="outline" className="text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-muted-foreground">{feature}</span>
                </div>
              ))}
            </div>

            <button
              onClick={() => handleEnrollClick('Standalone Internship')}
              className="w-full px-8 py-4 bg-card border-2 border-primary text-primary rounded-lg font-headline font-semibold text-lg hover:bg-primary/5 transition-all duration-300 flex items-center justify-center space-x-2"
            >
              <span>Get Standalone Internship</span>
              <Icon name="ArrowRightIcon" size={20} variant="outline" />
            </button>

            <p className="text-xs text-muted-foreground text-center mt-4">
              One-time payment • No recurring charges
            </p>
          </div>
        </div>

        {/* Value Comparison */}
        <div className="mt-16 text-center">
          <div className="inline-block bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-2 border-green-500/30 rounded-xl p-6">
            <p className="text-lg font-headline font-semibold text-foreground mb-2">
              💡 Smart Choice: Save ₹5,000 + Get Complete Learning!
            </p>
            <p className="text-sm text-muted-foreground">
              Enroll in any course and get the full internship experience absolutely FREE. 
              Learn comprehensively while building your portfolio.
            </p>
          </div>
        </div>
      </div>

      {isHydrated && (
        <EnrollmentModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <EnrollmentForm onClose={() => setIsModalOpen(false)} />
        </EnrollmentModal>
      )}
    </section>
  );
}
